import * as React from 'react';
import { findDOMNode } from 'react-dom';
import { MdExpandMore, MdExpandLess } from 'react-icons/md';
import { scrollIntoView } from 'app/infrastructure/platform/browser-dom';
import { DialogExpander } from 'ui/common/dialog-expander/dialog-expander';
import { ComplexValue, RowComponentProps } from 'ui/common/types';
import { ButtonTertiary } from 'ui';
import styles from './tile.scss';
import { debounceRender } from 'ui/common/debounce-render';
import { ExpanderBackground } from 'ui/common/dialog-expander/expander-background/expander-background';
import { ContextMenu } from 'ui/tile/context-menu';
import { ButtonSecondary, ValidationLevel } from 'ui/common';

const DefaultHeader = ({primaryKey, showPrimaryKey}) =>  primaryKey && showPrimaryKey ?
  <div className={`${styles.key}`}>#{primaryKey.value}</div> : null;

class TileComponent extends React.Component<TileProps, TileState> {
  private ref: HTMLElement;
  public static defaultProps: Partial<TileProps> = {
    enabledChildEntityOperations: { remove: false },
    textKeyLabel: ''
  };

  public state: Readonly<TileState> = {
    hideImmediately: false
  };

  private handleSelect = () => {
    this.setState({hideImmediately: false}, () =>
      this.props.onSelectDocumentItem && this.props.onSelectDocumentItem()
    );
    scrollIntoView( findDOMNode(this.ref) as HTMLElement);
  };

  private handleEdit = () => {
    this.props.onEditDocumentItem && this.props.onEditDocumentItem();
  };

  private handleDelete = () => {
    this.props.onDeleteDocumentItem && this.props.onDeleteDocumentItem();
  };

  private handleClose = () => {
    if (this.props.activate) {
       this.setState({hideImmediately: true}, () =>
       this.props.onSelectDocumentItem && this.props.onSelectDocumentItem(null) );
    }
  };

  private addRef = (el: HTMLElement) => this.ref = el;

  private hasChildErrors () {
    const {validationDescendants} = this.props;
    return validationDescendants && validationDescendants.length && validationDescendants.some(v => v.level === ValidationLevel.error);
  }

  private hasChildWarnings() {
    const {validationDescendants} = this.props;
    return validationDescendants && validationDescendants.length && validationDescendants.some(v => v.level === ValidationLevel.warning);
  }

  private renderDetailsButton = (shouldRenderOpenDetailView: boolean) => {
    const { details, textShowDetails, textHideDetails } = this.props;

    const detailsButtonStyle = this.hasChildErrors() ?
        styles.hasChildErrors :
        (this.hasChildWarnings() ? styles.hasChildWarnings : '');

    const showDetails = details ? <div className={styles.detailsButton}>
                          <ButtonTertiary classNames={detailsButtonStyle} onClick={this.handleSelect}>
                            {textShowDetails}<MdExpandMore/>
                          </ButtonTertiary>
                        </div> : null;
    const hideDetails = <div className={`${styles.detailsButton}`}>
                          <ButtonTertiary classNames={detailsButtonStyle} onClick={this.handleClose}>
                            {textHideDetails}<MdExpandLess/>
                          </ButtonTertiary>
                        </div>;
    return shouldRenderOpenDetailView ? hideDetails : showDetails;
  };

  // The Tile has become to procedurally heavy.
  // It takes on too many concerns and carries the logical weight of all it's use cases combined.
  // Refactoring should take place when changing the UX from expander to modal dialogs.
  // The inline comments are added to assist that refactoring later on.

  public render() {
    const {
      componentId,
      details,
      children,
      activate,
      primaryKey,
      showPrimaryKey = true,
      showDetailsButton = true,
      showDetailsEditButton = false,
      textKeyLabel,
      textDone,
      textDetailsEditButton,
      print,
      expandAll
    } = this.props;
    // Detail dialog styles and title when primaryKey prop is provided
    const primaryKeyValue = (primaryKey && primaryKey.value) || '';
    const dialogTitle = `${textKeyLabel} #${primaryKeyValue}`;
    const titleStyle = React.Children.toArray(children).length > 1 ? 'has-title' : '';
    const tileContentClassNamesWhenPrimaryKeyIsShown = (showPrimaryKey && primaryKey) ? styles.tileWithKey : '';

    // Specific styles when a context menu is rendered
    const contextMenuClassNames = this.props?.enabledChildEntityOperations?.remove ? styles.hasMeatball : '';

    const canActivate = showDetailsButton && !this.state.hideImmediately;
    const isActivated = canActivate && activate;
    const shouldRenderOpenDetailView = details && (print || expandAll || isActivated);

    const shouldRenderDetailsEditButton = shouldRenderOpenDetailView && showDetailsEditButton;
    const detailsEditButton = shouldRenderDetailsEditButton ?
      <ButtonSecondary classNames={styles.editButton} onClick={this.handleEdit}>{textDetailsEditButton}</ButtonSecondary> : null;

    // From above logical evaluations; construct a expander to display details
    const detailView = shouldRenderOpenDetailView ?
                                <DialogExpander
                                  expandAll={expandAll}
                                  onClose={this.handleClose}
                                  title={dialogTitle}
                                  textDone={textDone}
                                  extraFooterActions={[detailsEditButton]}
                                  className="tile-details-view">
                                  {expandAll && <ContextMenu {...this.props} onDelete={this.handleDelete}/>}
                                  {details}
                                  {expandAll && <div className={styles.editButtonExpandAll}>{detailsEditButton}</div>}
                                </DialogExpander>
                              : null;

    // Details wrapper style
    const openDetailWrapperStyles = shouldRenderOpenDetailView && !expandAll ? styles.activeTile : '';

    // Tile styling - conditional based on open state + conditional child components to render.
    const tileWrapperClasses = `${this.props.className} ${styles.tileWrapper} ${openDetailWrapperStyles}`;
    const expandDetailsButton = this.renderDetailsButton(shouldRenderOpenDetailView);

    if (expandAll && details) {
      // Only show details - no header, no context menu - no modal backdrop
      return <div id={componentId} ref={this.addRef} className={tileWrapperClasses}>
        {detailView}
      </div>;
    }

    return  <>
              {/* When expanded/open - render the close handle before the tile */}
              {shouldRenderOpenDetailView ? <div tabIndex={0} onFocus={this.handleClose}/> : ''}

              <div id={componentId} ref={this.addRef} className={tileWrapperClasses}>
                  <div className={`${styles.tile} ${contextMenuClassNames}`}>
                      <div className={`${styles.tileContent} ${tileContentClassNamesWhenPrimaryKeyIsShown}`}>
                        {/* Tile content - header, context menu and children*/}
                        <DefaultHeader {...this.props as any} />
                        <ContextMenu {...this.props} onDelete={this.handleDelete}/>
                        <div className={`tilevalues ${styles.tileValuesWrapper} ${titleStyle}`}>
                          {children}
                        </div>
                      </div>
                      {/* Button to expand is not part of the content - but rendered below */}
                      {showDetailsButton && expandDetailsButton}
                  </div>
                  {/* If expanded (active, expand-all, print) - renders the details */}
                  {detailView}
                  {/* Modal backdrop */}
                  {(shouldRenderOpenDetailView && !(print || expandAll)) && <ExpanderBackground onClick={this.handleClose}/>}
                </div>
            </>;
  }
}

/**
 * Required translated texts for this component
 */
export interface TileTranslations {
  textDelete: string;
  textDeleteMessage: string;
  textContinue: string;
  textCancel: string;
  textKeyLabel?: string;
  textDone?: string;
  textShowDetails?: string;
  textHideDetails?: string;
  textDetailsEditButton?: string;
}

/**
 * @interface TileProps
 * Required properties for Tile component.
 */
export interface TileProps extends RowComponentProps, TileTranslations {
  primaryKey: ComplexValue<number>;
  showPrimaryKey?: boolean;
  showPrimaryKeyLongDescription?: boolean;
  linkifyLongDescription?: boolean;
  showDetailsButton?: boolean;
  showDetailsEditButton?: boolean;
  extendedContextMenuOperations?: { text: string; callback: () => void }[];
  print: boolean;
  expandAll: boolean;
}

export interface TileState {
  /**
   * When expanded, don't wait the redux store update. Just close the view. UX optimization.
   */
  hideImmediately?: boolean;
}

const t = debounceRender(TileComponent);
export {t as Tile};
